import { z } from 'zod';
import { GraphSourceTool } from './source-tool.js';
/**
 * Work-artifact node schemas — the things people DO that flow through
 * the graph. Each carries the minimum fields the AI synthesis layer
 * needs to cite them; richer connector-specific payloads can be stored
 * on the Neo4j node as additional properties without changing the
 * shared shape.
 *
 * Every artifact has the same multi-tenant header:
 *   - `orgId`: the LeanOpx tenant
 *   - `source`: which tool emitted it
 *   - `externalId`: the tool's own id (drives the `(orgId, externalId)` uniqueness constraint)
 */
const NodeHeader = {
    id: z.string().min(1),
    orgId: z.string().min(1),
    source: GraphSourceTool,
    externalId: z.string().min(1),
};
/** Salesforce / Stripe customer account. */
export const Account = z.object({
    ...NodeHeader,
    name: z.string().min(1),
    domain: z.string().optional(),
    industry: z.string().optional(),
    createdAt: z.string(),
});
/** Salesforce opportunity / pipeline deal. */
export const Deal = z.object({
    ...NodeHeader,
    name: z.string().min(1),
    accountId: z.string().optional(),
    ownerPersonId: z.string().optional(),
    stage: z.string().min(1),
    amount: z.number().nonnegative().optional(),
    currency: z.string().length(3).optional(),
    closeDate: z.string().optional(),
    createdAt: z.string(),
});
/** Slack/Teams/Discord channel. */
export const Channel = z.object({
    ...NodeHeader,
    name: z.string().min(1),
    topic: z.string().optional(),
    isPrivate: z.boolean().default(false),
    accountId: z.string().optional(),
});
/** Chat message in a Channel. */
export const Message = z.object({
    ...NodeHeader,
    channelId: z.string().min(1),
    authorPersonId: z.string().optional(),
    text: z.string(),
    postedAt: z.string(),
    threadParentId: z.string().optional(),
});
/** Email message (Gmail/Outlook/etc.). */
export const EmailMessage = z.object({
    ...NodeHeader,
    threadId: z.string().min(1),
    subject: z.string(),
    fromPersonId: z.string().optional(),
    toPersonIds: z.array(z.string()).default([]),
    sentAt: z.string(),
    snippet: z.string().optional(),
});
/** Git commit. */
export const Commit = z.object({
    ...NodeHeader,
    sha: z.string().min(7),
    authorPersonId: z.string().optional(),
    repo: z.string().min(1),
    message: z.string(),
    committedAt: z.string(),
});
/** Pull request. */
export const PullRequest = z.object({
    ...NodeHeader,
    repo: z.string().min(1),
    number: z.number().int().positive(),
    title: z.string().min(1),
    authorPersonId: z.string().optional(),
    state: z.enum(['open', 'closed', 'merged']),
    openedAt: z.string(),
    mergedAt: z.string().optional(),
});
/** Stripe charge. */
export const Charge = z.object({
    ...NodeHeader,
    accountId: z.string().optional(),
    subscriptionId: z.string().optional(),
    amount: z.number().nonnegative(),
    currency: z.string().length(3),
    status: z.enum(['succeeded', 'pending', 'failed', 'refunded']),
    chargedAt: z.string(),
});
/** Stripe subscription. */
export const Subscription = z.object({
    ...NodeHeader,
    accountId: z.string().optional(),
    plan: z.string().min(1),
    status: z.enum(['active', 'past_due', 'canceled', 'trialing']),
    startedAt: z.string(),
    endsAt: z.string().optional(),
    mrr: z.number().nonnegative().optional(),
});
